import { Fragment, type ReactNode } from "react";
import { useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSeo } from "../hooks/useSeo";
import { buildCanonicalUrl } from "../seo/config";
import type { LayoutOutletContext } from "../layout/types";

type CategoryItem = {
  title: string;
  description: string;
};

type PrizeItem = {
  title: string;
  details: string;
  note?: string;
};

interface TextSection {
  heading: string;
  body: string[];
}

interface ListSection<TItem> {
  heading: string;
  items: TItem[];
}

interface SimpleSection {
  heading: string;
  description: string;
}

type JoinLink = {
  id: string;
  label: string;
  href?: string;
  alt?: string;
};

const withTrailingDots = (value: ReactNode, dots = 80) => (
  <>
    {value}
    <span
      className="details-list__dots"
      aria-hidden="true"
      role="presentation"
    >
      {".".repeat(Math.max(dots, 0))}
    </span>
  </>
);

export const CompetitionDetails = () => {
  const { t } = useTranslation();
  const { locale, localePrefix } = useOutletContext<LayoutOutletContext>();
  const pageTitle = t("pages.competitionDetails.title");
  const siteTitle = t("site.title");
  const sectionClasses = "page-section";

  useSeo({
    title: `${pageTitle} | ${siteTitle}`,
    description: t("pages.competitionDetails.intro"),
    canonical: buildCanonicalUrl("competition-details/", locale),
    locale,
  });

  const categories = t("pages.competitionDetails.categories", {
    returnObjects: true,
  }) as ListSection<CategoryItem>;

  const prizes = t("pages.competitionDetails.prizes", {
    returnObjects: true,
  }) as ListSection<PrizeItem>;

  const howToJoin = t("pages.competitionDetails.howToJoin", {
    returnObjects: true,
  }) as TextSection;

  const rules = t("pages.competitionDetails.rules", {
    returnObjects: true,
  }) as { heading: string; items: string[] };

  const deadline = t("pages.competitionDetails.deadline", {
    returnObjects: true,
  }) as SimpleSection;

  const announcement = t("pages.competitionDetails.announcement", {
    returnObjects: true,
  }) as TextSection;

  const enquiry = t("pages.competitionDetails.enquiry", {
    returnObjects: true,
  }) as SimpleSection;

  const joinNowTranslationLinks = t("pages.joinNow.links", {
    returnObjects: true,
  }) as JoinLink[];

  const joinAssetBase = `${import.meta.env.BASE_URL}assets/join-now/`;
  const siteBase = import.meta.env.BASE_URL.replace(/\/$/, "");
  const localeSegment = localePrefix ? `/${localePrefix}` : "";

  type JoinLinkId = "family" | "secondary" | "open";
  const joinFilenames: Record<JoinLinkId, string> =
    locale === "en"
      ? {
          family: "join-family-en.png",
          secondary: "join-sec-en.png",
          open: "join-open-en.png",
        }
      : {
          family: "join-family.png",
          secondary: "join-sec.png",
          open: "join-open.png",
        };

  const joinButtons = joinNowTranslationLinks.map((link) => {
    const id = link.id as JoinLinkId;
    const image = `${joinAssetBase}${joinFilenames[id] ?? joinFilenames.family}`;
    const href = `${siteBase}${localeSegment}/join-now/`;

    return {
      ...link,
      image,
      href,
      isExternal: false,
    };
  });

  const renderDetailsRow = (
    title: string,
    description: string,
    note?: string,
    className?: string,
  ) => (
    <div className={`details-list__row${className ? ` ${className}` : ""}`}>
      <span className="details-list__title">
        {withTrailingDots(
          <>
            {title}
            {note ? <em className="details-list__note"> {note}</em> : null}
          </>,
        )}
      </span>
      <span className="details-list__description">{description}</span>
    </div>
  );

  return (
    <article className="page page--competition">
      <div className="page__sections">
        <section className={sectionClasses}>
          <h2 className="page-section__title">{categories.heading}</h2>
          <ul className="page-section__list page-section__list--details page-section__list--categories">
            {categories.items.map(({ title, description }) => (
              <li key={title} className="page-section__item">
                {renderDetailsRow(title, description)}
              </li>
            ))}
          </ul>
        </section>

        <section className={sectionClasses}>
          <h2 className="page-section__title page-section__title--prizes">
            {prizes.heading}
          </h2>
          <ul className="page-section__list page-section__list--details page-section__list--prizes">
            {prizes.items.map(({ title, details, note }) => (
              <li key={title} className="page-section__item">
                {renderDetailsRow(title, details, note, "details-list__row--prize")}
              </li>
            ))}
          </ul>
        </section>

        <section className={sectionClasses}>
          <h2 className="page-section__title page-section__title--prizes">
            {howToJoin.heading}
          </h2>
          <div className="page-section__paragraphs page-section__paragraphs--indented page-section__paragraphs--bulleted">
            {howToJoin.body.map((paragraph, index) => (
              <Fragment key={`${index}-${paragraph.slice(0, 12)}`}>
                <p
                  className={`page-section__paragraph--bullet${index === 0 ? " page-section__paragraph--bullet-primary" : ""}`}
                >
                  {paragraph}
                </p>
                {index === 0 ? (
                  <div className="join-now__links join-now__links--inline">
                    {joinButtons.map(({ id, label, href, image, alt, isExternal }) => (
                      <a
                        key={id}
                        href={href}
                        className="join-now__link"
                        aria-label={label}
                        target={isExternal ? "_blank" : undefined}
                        rel={isExternal ? "noopener noreferrer" : undefined}
                      >
                        <img
                          src={image}
                          alt={alt || label}
                          className="join-now__image"
                        />
                      </a>
                    ))}
                  </div>
                ) : null}
              </Fragment>
            ))}
          </div>
        </section>

        <section className={sectionClasses}>
          <h2 className="page-section__title page-section__title--prizes">
            {rules.heading}
          </h2>
          <div className="page-section__paragraphs page-section__paragraphs--indented page-section__paragraphs--rules page-section__paragraphs--bulleted">
            {rules.items.map((rule, index) => (
              <p key={index} className="page-section__paragraph--bullet">
                {rule}
              </p>
            ))}
          </div>
        </section>

        <section className={sectionClasses}>
          <h2 className="page-section__title page-section__title--prizes">
            {deadline.heading}
          </h2>
          <div className="page-section__paragraphs page-section__paragraphs--indented page-section__paragraphs--bulleted">
            <p className="page-section__paragraph--bullet">{deadline.description}</p>
          </div>
        </section>

        <section className={sectionClasses}>
          <h2 className="page-section__title page-section__title--prizes">
            {announcement.heading}
          </h2>
          <div className="page-section__paragraphs page-section__paragraphs--indented page-section__paragraphs--bulleted">
            {announcement.body.map((paragraph, index) => (
              <p key={index} className="page-section__paragraph--bullet">
                {paragraph}
              </p>
            ))}
          </div>
        </section>

        <section className={sectionClasses}>
          <h2 className="page-section__title page-section__title--prizes">
            {enquiry.heading}
          </h2>
          <div className="page-section__paragraphs page-section__paragraphs--indented page-section__paragraphs--bulleted">
            <p className="page-section__paragraph--bullet">{enquiry.description}</p>
          </div>
        </section>
      </div>
    </article>
  );
};







